import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import { 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, IconButton, TablePagination, Typography, Box, Button, 
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle 
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Client } from '../../types/client';
import apiService from '../../services/api';
import { useRBAC } from '../../auth/RBACProvider';

interface ClientListProps {
  onEditClient: (client: Client) => void;
  refreshTrigger: number;
}

const ClientList: React.FC<ClientListProps> = ({ onEditClient, refreshTrigger }) => {
  const { hasGlobalPermission, isAdmin, isSuperAdmin } = useRBAC();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);

  // Check permissions
  const canManageClients = hasGlobalPermission('manage_clients');
  const canDeleteClients = isAdmin() || isSuperAdmin(); // Only admin and super_admin can delete

  useEffect(() => {
    fetchClients();
  }, [refreshTrigger]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const data = await apiService.getClients();
      // Handle both string array and object array formats
      let clientsData: Client[];
      if (Array.isArray(data)) {
        clientsData = data.map((c: any) => {
          if (typeof c === 'string') {
            // API returns string array - create minimal Client objects with new structure
            return {
              account: c,
              client_name: c,
              client_url: '',
              contract_start_date: undefined,
              contract_end_date: undefined,
              client_point_of_contact: { name: '', email: '', contact_number: '' },
              wizrdom_point_of_contact: { name: '', email: '', contact_number: '' },
              services_scope: {
                organic_social_media: { linkedin: false, instagram: false, facebook: false, youtube: false, x: false, others: false },
                paid_marketing: { meta: false, linkedin_ads: false, google_ads: false },
                content: { social_media_posts: false, blogs: false, landing_pages: false, emails: false, brochures_datasheets: false, whitepapers_ebooks: false, videos: false, video_shoots: false, sales_decks: false, investor_decks: false },
                seo: false,
                website_development: false,
                email_marketing: false,
                telemarketing: false,
                branding: { logo: false, brand_identity: false, brand_names: false, taglines: false, branding_guidelines: false, brand_mascot: false },
                events: { pre_event_marketing: false, event_booth_support: false, post_event_content: false, post_event_outreach: false }
              },
              email_campaign_thresholds: { mofu: 0 },
              email_campaign_scoring: { opened: 0, clicked: 0, unsubscribed: 0, spam: 0, bounced: 0 },
              campaign_budgets: { email_campaigns: 0, telemarketing: 0, linkedin: 0, google: 0, meta: 0, currency: 'INR' }
            } as Client;
          } else {
            // API returns full Client objects from database
            return c as Client;
          }
        });
      } else {
        clientsData = [];
      }
      setClients(clientsData);
      setError(null);
    } catch (err) {
      setError('Failed to fetch clients. Please try again later.');
      logger.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openDeleteDialog = (client: Client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setClientToDelete(null);
  };

  const [deleting, setDeleting] = useState(false);
  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    setDeleting(true);
    try {
      const account = clientToDelete.account;
      await apiService.deleteClient(account);
      setClients(prev => prev.filter(c => c.account !== account));
      closeDeleteDialog();
    } catch (err) {
      logger.error('Error deleting client:', err);
      setError('Failed to delete client. Please try again later.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading && clients.length === 0) {
    return <Typography>Loading clients...</Typography>;
  }

  if (error && clients.length === 0) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ color: 'var(--e-global-color-primary)', fontWeight: 600 }}>
          Clients List
        </Typography>
        <TableContainer component={Paper}>
          <Table aria-label="client table" size="small" sx={{
            backgroundColor: 'rgba(255,255,255,0.9)',
            '& thead th': {
              backgroundColor: 'var(--e-global-color-primary)',
              color: '#fff',
              fontWeight: 600
            },
            '& tbody td': { borderBottom: '1px solid rgba(0,0,0,0.05)' }
          }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white' }}>Client Name</TableCell>
                <TableCell sx={{ color: 'white' }}>Account</TableCell>
                <TableCell sx={{ color: 'white' }}>Website</TableCell>
                <TableCell sx={{ color: 'white' }}>Contract Period</TableCell>
                <TableCell sx={{ color: 'white' }}>Services</TableCell>
                <TableCell sx={{ color: 'white' }}>Email Threshold (MOFU)</TableCell>
                <TableCell sx={{ color: 'white' }}>Email Scoring</TableCell>
                <TableCell sx={{ color: 'white' }}>Budgets</TableCell>
                <TableCell sx={{ color: 'white' }}>Client Contact</TableCell>
                <TableCell sx={{ color: 'white' }}>Wizrdom Contact</TableCell>
                <TableCell sx={{ color: 'white' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((client) => {
                // Count enabled services
                const servicesCount = client.services_scope ? (
                  Object.values(client.services_scope.organic_social_media || {}).filter(v => v).length +
                  Object.values(client.services_scope.paid_marketing || {}).filter(v => v).length +
                  Object.values(client.services_scope.content || {}).filter(v => v).length +
                  (client.services_scope.seo ? 1 : 0) +
                  (client.services_scope.website_development ? 1 : 0) +
                  (client.services_scope.email_marketing ? 1 : 0) +
                  (client.services_scope.telemarketing ? 1 : 0) +
                  Object.values(client.services_scope.branding || {}).filter(v => v).length +
                  Object.values(client.services_scope.events || {}).filter(v => v).length
                ) : 0;
                
                // Format budgets
                const budgets = client.campaign_budgets;
                const budgetSummary = budgets ? [
                  budgets.email_campaigns > 0 ? `Email: ${budgets.email_campaigns}` : null,
                  budgets.telemarketing > 0 ? `Tel: ${budgets.telemarketing}` : null,
                  budgets.linkedin > 0 ? `LI: ${budgets.linkedin}` : null,
                  budgets.google > 0 ? `G: ${budgets.google}` : null,
                  budgets.meta > 0 ? `M: ${budgets.meta}` : null
                ].filter(Boolean).join(', ') : '-';

                return (
                  <TableRow key={client.account} hover>
                    <TableCell>{client.client_name}</TableCell>
                    <TableCell>{client.account}</TableCell>
                    <TableCell>{client.client_url || '-'}</TableCell>
                    <TableCell>
                      {client.contract_start_date || client.contract_end_date ? 
                        `${client.contract_start_date || '?'} - ${client.contract_end_date || '?'}` : '-'}
                    </TableCell>
                    <TableCell>{servicesCount > 0 ? `${servicesCount} services` : '-'}</TableCell>
                    <TableCell>{client.email_campaign_thresholds?.mofu || '-'}</TableCell>
                    <TableCell>
                      {client.email_campaign_scoring ? 
                        `${client.email_campaign_scoring.opened}/${client.email_campaign_scoring.clicked}/${client.email_campaign_scoring.unsubscribed}/${client.email_campaign_scoring.spam}/${client.email_campaign_scoring.bounced}` : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{budgetSummary || '-'}</TableCell>
                    <TableCell>
                      {client.client_point_of_contact?.name || client.client_point_of_contact?.email ? 
                        `${client.client_point_of_contact.name || ''} ${client.client_point_of_contact.email || ''}`.trim() : '-'}
                    </TableCell>
                    <TableCell>
                      {client.wizrdom_point_of_contact?.name || client.wizrdom_point_of_contact?.email ? 
                        `${client.wizrdom_point_of_contact.name || ''} ${client.wizrdom_point_of_contact.email || ''}`.trim() : '-'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {canManageClients ? (
                        <>
                          <IconButton
                            aria-label="edit"
                            color="primary"
                            onClick={() => onEditClient(client)}
                          >
                            <EditIcon />
                          </IconButton>
                          {canDeleteClients && (
                            <IconButton
                              aria-label="delete"
                              color="error"
                              onClick={() => openDeleteDialog(client)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          View Only
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {clients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    No clients found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={clients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete the client "{clientToDelete?.client_name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteClient} color="error" autoFocus disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientList;
